import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { Firearm, FirearmInput, FirearmStatus } from '../types/cac';

/** Acervo CAC → Armas. Todas as rotas exigem `features.cac` (403 "não liberado" caso contrário). */
class FirearmService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /firearms?status=&include_deleted= — com `cac_documents` vigentes e `documents_count`. */
  async getAll(status?: FirearmStatus | '', includeDeleted = false): Promise<Firearm[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/firearms${toQuery({ status: status || undefined, include_deleted: includeDeleted })}`);
    return readList<Firearm>(response, 'Falha ao listar as armas');
  }

  /** GET /firearms/:id — ficha com todos os documentos CAC e os arquivos vinculados. */
  async getById(id: number): Promise<Firearm> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/firearms/${id}`);
    const f = await readData<Firearm>(response, 'Falha ao carregar a arma');
    return { ...f, cac_documents: f?.cac_documents ?? [], documents: f?.documents ?? [] };
  }

  /** POST /firearms */
  async create(input: FirearmInput): Promise<Firearm> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/firearms', input);
    return readData<Firearm>(response, 'Falha ao cadastrar a arma');
  }

  /** PUT /firearms/:id */
  async update(id: number, input: FirearmInput): Promise<Firearm> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/firearms/${id}`, input);
    return readData<Firearm>(response, 'Falha ao atualizar a arma');
  }

  /** DELETE /firearms/:id — exclusão lógica (409 se houver documento CAC vigente). */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/firearms/${id}`);
    await ensureOk(response, 'Falha ao excluir a arma');
  }
}

export const firearmService = new FirearmService();
export default firearmService;
